import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { partnerErrorMessage } from '../state/partnerError';
import { partnerGateway } from '../services';
import { invitationLink } from '../invitationLink';
import { usePartner } from '../state/usePartner';
import { PARTNER_SERVICES, type PartnerClientInvitationResult } from '../types';

/**
 * Add Client, steps 1 to 3 of the brief: who the client is, which services you ask to provide, then send the invitation. The client's owner decides at their end: nothing is enabled by
 * choosing a service here, and no payment or filing becomes live. Setup, readiness and the rest happen after the client accepts and are not built yet.
 */
export function AddClientPage() {
  const { membership, can } = usePartner();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [services, setServices] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<PartnerClientInvitationResult | null>(null);
  const [copied, setCopied] = useState(false);

  if (!can('partner.clients.manage') || membership.status !== 'Active') {
    return (
      <>
        <h1>Add Client</h1>
        <p role="alert" className="pp-error">Your role cannot add clients.</p>
      </>
    );
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      setResult(await partnerGateway.inviteClient(membership.partnerOrganizationId, { clientName: name.trim(), invitedEmail: email.trim(), services }));
    } catch (e) {
      setError(partnerErrorMessage(e, 'The invitation could not be created.'));
    } finally {
      setBusy(false);
    }
  }

  if (result) {
    const link = invitationLink(result.invitationToken);
    return (
      <>
        <h1>Invitation created</h1>
        <section className="pp-card" aria-labelledby="pp-invite-link">
          <h2 id="pp-invite-link">Send this link to {result.client.invitedEmail}</h2>
          <p className="pp-warn" role="note">
            <strong>This link is shown once.</strong> It is not stored and cannot be shown again. If it is lost, re-send the invitation from the client's page to create a new one (the old link stops working).
          </p>
          <p>
            Only a signed-in owner of the client company, using the email address you entered, can accept it. Email delivery is not available yet, so please send the link yourself, for example by your own email.
          </p>
          <label>
            Invitation link
            <input readOnly value={link} onFocus={(e) => e.currentTarget.select()} />
          </label>
          <div className="pp-actions">
            <button
              type="button"
              className="pp-button pp-button--primary"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(link);
                  setCopied(true);
                } catch {
                  setCopied(false);
                }
              }}
            >
              Copy link
            </button>
            {copied && <span role="status" className="pp-ok">Copied.</span>}
            <Link className="pp-button" to={`/partner/clients/${result.client.id}`}>Open the client</Link>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <h1>Add Client</h1>
      <form className="pp-card pp-form" onSubmit={(e) => void submit(e)} noValidate>
        <h2>Step 1: Client basics</h2>
        <label>
          Client business name
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={200} required autoComplete="off" />
        </label>
        <label>
          Client owner's email
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={256} required autoComplete="off" />
        </label>
        <p className="pp-muted">
          Only these two details are needed to invite a client. The rest of the client's company information (address, employees, pay schedules, taxes) is entered during company setup after they accept, which is not built yet.
        </p>

        <fieldset>
          <legend>Step 2: Services you ask to provide</legend>
          <p className="pp-muted">The client's owner chooses which of these to allow. Choosing a service here does not turn on live payments or filings.</p>
          {PARTNER_SERVICES.map((service) => (
            <label key={service.code} className="pp-check">
              <input
                type="checkbox"
                checked={services.includes(service.code)}
                onChange={(e) => setServices((current) => (e.target.checked ? [...current, service.code] : current.filter((c) => c !== service.code)))}
              />
              {service.label}
            </label>
          ))}
        </fieldset>

        {error && <p role="alert" className="pp-error">{error}</p>}
        <div className="pp-actions">
          <button type="submit" className="pp-button pp-button--primary" disabled={busy || !name.trim() || !email.trim() || services.length === 0}>
            {busy ? 'Creating…' : 'Step 3: Create the invitation'}
          </button>
          <Link className="pp-button" to="/partner/clients">Cancel</Link>
        </div>
      </form>
    </>
  );
}
